import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Link, Truck, Search, Filter } from 'lucide-react';
import { Vehicle, TraccarDevice } from '../../types';
import { vehicleApi, traccarApi } from '../../services/api';
import LoadingSpinner from '../ui/LoadingSpinner';
import VehicleForm from './VehicleForm';

const VehicleManagement: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [traccarDevices, setTraccarDevices] = useState<TraccarDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    fetchVehicles();
    fetchTraccarDevices();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await vehicleApi.getAll();
      if (response.success && response.data) {
        setVehicles(response.data);
      }
    } catch (err) {
      setError('Failed to fetch vehicles');
    }
  };

  const fetchTraccarDevices = async () => {
    try {
      const response = await traccarApi.getDevices();
      if (response.success && response.data) {
        setTraccarDevices(response.data);
      }
    } catch (err) {
      console.warn('Failed to fetch Traccar devices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = async (vehicleData: Partial<Vehicle>) => {
    try {
      const response = await vehicleApi.create(vehicleData);
      if (response.success && response.data) {
        setVehicles([...vehicles, response.data]);
        setShowAddModal(false);
      }
    } catch (err) {
      setError('Failed to add vehicle');
    }
  };

  const handleEditVehicle = async (vehicleData: Partial<Vehicle>) => {
    if (!editingVehicle) return;
    
    try {
      const response = await vehicleApi.update(editingVehicle.id, vehicleData);
      if (response.success && response.data) {
        setVehicles(vehicles.map(v => v.id === editingVehicle.id ? response.data! : v));
        setEditingVehicle(null);
      }
    } catch (err) {
      setError('Failed to update vehicle');
    }
  };

  const handleDeleteVehicle = async (id: number) => {
    if (!confirm('Are you sure you want to delete this vehicle?')) return;
    
    try {
      const response = await vehicleApi.delete(id);
      if (response.success) {
        setVehicles(vehicles.filter(v => v.id !== id));
      }
    } catch (err) {
      setError('Failed to delete vehicle');
    }
  };

  const handleLinkToTraccar = async (vehicleId: number, traccarDeviceId: number) => {
    try {
      const response = await vehicleApi.linkToTraccar(vehicleId, traccarDeviceId);
      if (response.success && response.data) {
        setVehicles(vehicles.map(v => v.id === vehicleId ? response.data! : v));
      }
    } catch (err) {
      setError('Failed to link vehicle to Traccar device');
    }
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = (vehicle.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (vehicle.registration?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || vehicle.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getTraccarDevice = (traccarDeviceId?: number) => {
    return traccarDevices.find(d => d.id === traccarDeviceId);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-fleet-dark">Vehicle Management</h1>
          <p className="text-fleet-neutral">Manage your fleet vehicles and link them to tracking devices</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Vehicle
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-fleet p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fleet-neutral h-5 w-5" />
              <input
                type="text"
                placeholder="Search vehicles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Filter className="h-5 w-5 text-fleet-neutral" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="maintenance">Maintenance</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800 ml-4"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Vehicles Table */}
      <div className="bg-white shadow-fleet rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vehicle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Traccar Device
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredVehicles.map((vehicle) => {
                const traccarDevice = getTraccarDevice(vehicle.traccar_device_id);
                return (
                  <tr key={vehicle.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Truck className="h-8 w-8 text-fleet-primary mr-3" />
                        <div>
                          <div className="text-sm font-medium text-fleet-dark">
                            {vehicle.name || 'Unnamed Vehicle'}
                          </div>
                          <div className="text-sm text-fleet-neutral">
                            {vehicle.make} {vehicle.model} ({vehicle.year})
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-fleet-dark">
                      {vehicle.registration || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(vehicle.status)}`}>
                        {vehicle.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-fleet-dark">
                      {traccarDevice ? (
                        <div>
                          <div className="font-medium">{traccarDevice.name}</div>
                          <div className="text-fleet-neutral">{traccarDevice.uniqueId}</div>
                        </div>
                      ) : (
                        <span className="text-red-500">Not linked</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => setEditingVehicle(vehicle)}
                        className="text-fleet-primary hover:text-fleet-secondary"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteVehicle(vehicle.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      {!vehicle.traccar_device_id && (
                        <button
                          onClick={() => {
                            // Open link modal - simplified for now
                            const deviceId = prompt('Enter Traccar Device ID:');
                            if (deviceId) {
                              handleLinkToTraccar(vehicle.id, parseInt(deviceId));
                            }
                          }}
                          className="text-blue-600 hover:text-blue-800"
                          title="Link to Traccar Device"
                        >
                          <Link className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Vehicle Modal */}
      {showAddModal && (
        <VehicleForm
          onSubmit={handleAddVehicle}
          onCancel={() => setShowAddModal(false)}
          traccarDevices={traccarDevices}
        />
      )}

      {/* Edit Vehicle Modal */}
      {editingVehicle && (
        <VehicleForm
          vehicle={editingVehicle}
          onSubmit={handleEditVehicle}
          onCancel={() => setEditingVehicle(null)}
          traccarDevices={traccarDevices}
        />
      )}
    </div>
  );
};

export default VehicleManagement;